import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IonButton,
  IonIcon,
  IonLabel,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonSpinner
} from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import { Subject, concatMap, of, takeUntil, tap } from 'rxjs';
import { InAppPurchaseComponent } from '@components/in-app-purchase/in-app-purchase.component';
import { TermsService } from '@components/terms/terms.service';
import { AssistantSubscriptionPurchaseCompleteQueryParams } from '@pages/assistant/subscription/purchase-complete/purchase-complete.page';
import { assistantSubscriptionActions } from '@pages/assistant/subscription/subscription.actions';
import { selectModalSubscriptionOpen } from '@pages/assistant/subscription/subscription.selectors';
import { selectReportSelectedReportType } from '@pages/report/report.selectors';
import { ShopOrderActions } from '@pages/shop/order/order.actions';
import { ProductListService } from '@pages/shop/product/product.service';
import { CurrencyPipe } from '@pipes/currency/currency';
import { EventService } from '@providers/event/event.service';
import { Session } from '@providers/session/session.service';
import {
  AppSubscription,
  PurchaseProduct
} from '../../../../../../interfaces/schema';
import { AssistantSubscriptionListPlanComponent } from '../../list-plan/list-plan.component';

/**
 * Plan recommendation section component of subscription page.
 */
@Component({
  selector: 'app-assistant-subscription-plan-recommendation-modal',
  standalone: true,
  imports: [
    CommonModule,
    AssistantSubscriptionListPlanComponent,
    IonIcon,
    IonModal,
    IonButton,
    IonSpinner,
    IonLabel,
    IonSegment,
    IonSegmentButton
  ],
  templateUrl: './modal.component.html',
  providers: [TermsService, ProductListService, CurrencyPipe],
  styles: [
    `
      ion-modal#assistant-subscription-plan-recommendation-modal {
        --height: auto;
        --border-radius: 25px;
        --max-height: calc(95vh - var(--ion-safe-area-top));
      }

      @media (max-height: 700px) {
        ion-modal#assistant-subscription-plan-recommendation-modal {
          --height: 100%;
          --max-height: 100%;
        }
      }

      ::ng-deep
        ion-modal#assistant-subscription-plan-recommendation-modal
        .ion-page {
        position: relative;
        display: block;
        contain: content;
      }

      @media (max-height: 700px) {
        .ion-content-scroll-host {
          height: 100% !important;
        }
      }

      .scrollable-content {
        max-height: 60vh;
      }

      @media (max-height: 700px) {
        .scrollable-content {
          max-height: 67vh;
        }
      }

      .footer-section {
        padding-bottom: 28px;
      }

      @media (max-height: 700px) {
        .footer-section {
          padding-bottom: 0;
        }
      }

      ion-content {
        position: relative;
        display: block;
        contain: content;
      }
      ion-content::part(scroll) {
        position: relative;
        display: block;
        contain: content;
        max-height: 100%;
      }
      ion-toolbar {
        padding-bottom: 0px !important;
      }

      .price {
        color: var(--ion-color-dark);
      }

      .discount {
        background: var(--ion-color-gradient-pink);
        color: #ffffff;
      }

      ion-segment {
        height: 45px;
        padding: 5px;
        border: 0.5px solid #e5e5e5;
        border-radius: 40px;

        font-size: 12px;
      }

      ion-segment-button::part(indicator-background) {
        border-radius: 40px;
      }

      ion-segment-button::part(native) {
        font-size: 12px;
        font-weight: 400 !important;
        color: #494949 !important;
      }

      ion-segment-button.segment-button-checked::part(native) {
        font-weight: 600 !important;
        color: rgb(73 73 73 / 100%) !important;
      }

      ion-segment-button {
        ion-spinner {
          color: rgb(0 0 0 / 50%);
        }
      }

      .bg-gradient {
        background:
          url('/assets/images/nourish/usp/gradient2.png') no-repeat center
            bottom / 100% 20%,
          white !important;
      }
    `
  ]
})
export class AsisstantSubscriptionPlanRecommendationModalComponent implements OnInit {
  private readonly events = inject(EventService);
  private readonly router = inject(Router);
  private readonly service = inject(ProductListService);
  private readonly session = inject(Session);
  private readonly store = inject(Store);
  private readonly termService = inject(TermsService);

  private readonly inAppProductComponent = viewChild<InAppPurchaseComponent>(
    InAppPurchaseComponent
  );
  private readonly listPlanComponent =
    viewChild<AssistantSubscriptionListPlanComponent>(
      AssistantSubscriptionListPlanComponent
    );
  private readonly modal = viewChild(IonModal);

  protected readonly breakpoint = signal<number>(1);
  protected readonly isOpen = signal<boolean>(false);
  protected readonly isProcessingPayment = signal<boolean>(false);
  private readonly loading = signal<{ availablePlans: boolean }>({
    availablePlans: false
  });
  protected readonly selectedPlanCode = signal<string | null>(null);
  protected readonly selectedPlanId = signal<string | null>(null);
  protected readonly selectedPlanStoreProductId = signal<string>('');

  private readonly selectedReportType = this.store.selectSignal(
    selectReportSelectedReportType
  );

  destroyed: Subject<boolean>;
  loadingFetch = true;
  product: any = {};

  ableToPay = computed<boolean>(
    () => this.selectedPlanId() !== null && !this.loading().availablePlans
  );

  /**
   * Create new instance.
   */
  constructor() {
    this.destroyed = new Subject();
  }

  /**
   * @description Abbreviation of selected plan.
   * @readonly
   * @type {string}
   * @memberof AsisstantSubscriptionPlanRecommendationModalComponent
   */
  get abbreviation(): string {
    const listPlanComponent = this.listPlanComponent();
    if (listPlanComponent) {
      if (listPlanComponent.selectedPlan()) {
        const plan = listPlanComponent.selectedPlan();
        return plan?.abbreviation ?? '';
      }
    }
    return '';
  }

  /**
   * @description Total price of selected plan.
   * @readonly
   * @type {number}
   * @memberof AsisstantSubscriptionPlanRecommendationModalComponent
   */
  get totalPrice(): number {
    const listPlanComponent = this.listPlanComponent();
    if (listPlanComponent) {
      if (listPlanComponent.selectedPlan()) {
        const plan = listPlanComponent.selectedPlan();
        return plan?.totalPrice ?? 0;
      }
    }
    return 0;
  }

  /**
   * On init.
   */
  ngOnInit(): void {
    this.store
      .select(selectModalSubscriptionOpen)
      .pipe(takeUntil(this.destroyed))
      .subscribe((value) => {
        this.isOpen.set(value);
      });
  }

  /**
   * Fetch data.
   *
   * @returns {void}
   */
  fetchProduct(): void {
    of(true)
      .pipe(
        takeUntil(this.destroyed),
        tap(() => (this.loadingFetch = true)),
        concatMap(() =>
          this.service.product({ id: this.selectedPlanStoreProductId() })
        )
      )
      .subscribe({
        /**
         * @description Handle response data.
         * @param {PurchaseProduct | null} product Data.
         */
        next: (product: PurchaseProduct | null) => {
          if (!isNil(product) && !isEmpty(product)) {
            this.product = cloneDeep(product);
            this.loadingFetch = false;
            this.checkout();
          }
        },
        /**
         * @description Handle response error.
         */
        error: () => {
          this.loadingFetch = false;
        },
        /**
         * @description Complete.
         */
        complete: () => {
          this.loadingFetch = false;
        }
      });
  }

  /**
   * Checkout selected product.
   */
  checkout(): void {
    const product: any = cloneDeep(this.product);
    const isOnDNAReportPage = this.router.url === '/tabs/dna-report';
    const isOnNutritionGuidePage =
      this.router.url.includes('nourish/nutrition-guide') ||
      this.router.url.includes('nourish/nutrition-plus-guide');

    if (this.session.isAuthenticated()) {
      this.store.dispatch(ShopOrderActions.setCart({ product }));

      this.session.clear(this.session.keys.shop);

      const queryParams: Record<string, string | null> = {
        planId: this.selectedPlanCode()
      };

      if (isOnDNAReportPage || isOnNutritionGuidePage) {
        queryParams['reportType'] = this.selectedReportType()?.reportType ?? '';
      }

      this.router.navigate(['/shop/order'], {
        queryParams
      });
    }
  }

  /**
   * Close.
   */
  close(): void {
    this.modal()?.dismiss();
  }

  /**
   * Did dismiss.
   */
  didDismiss(): void {
    const modal = this.modal();
    if (modal) modal.showBackdrop = true;
    this.store.dispatch(
      assistantSubscriptionActions.setModalSubscription({
        isModalSubscriptionOpen: false
      })
    );
  }

  /**
   * On loading available plans changed.
   *
   * @param {boolean} event Event.
   */
  onLoadingAvailablePlansChanged(event: boolean): void {
    this.loading.update((v) => ({
      ...v,
      availablePlans: event
    }));
  }

  /**
   * On plan clicked.
   */
  onPlanClicked(): void {
    if (this.isProcessingPayment()) {
      this.events.publish(
        this.events.keys.TOAST,
        'Please wait. Transaction is pending.'
      );
    }
  }

  /**
   * Pay.
   */
  pay(): void {
    if (!this.ableToPay()) {
      return;
    }
    const inApp = this.inAppProductComponent();
    if (inApp) {
      inApp.click();
    }
  }

  /**
   * On purchased.
   */
  purchased(): void {
    const params: AssistantSubscriptionPurchaseCompleteQueryParams = {
      redirectTo: '/assistant/subscription'
    };
    this.router.navigate(['/assistant/subscription/purchase-complete'], {
      queryParams: params
    });
    this.store.dispatch(assistantSubscriptionActions.userPurchasedProducts());
  }

  /**
   * @description Select plan.
   * @param {AppSubscription} item Plan item.
   */
  selectPlan(item: AppSubscription): void {
    const listPlanComponent = this.listPlanComponent();
    if (listPlanComponent) {
      listPlanComponent.selectPlan(item as any);
    }
  }

  /**
   * @description Get subscription plans from list-plan component.
   * @returns {Array<AppSubscription>} Subscription plans.
   */
  subscriptionPlans(): Array<AppSubscription> {
    const listPlanComponent = this.listPlanComponent();
    return listPlanComponent ? listPlanComponent.subscriptionPlans() : [];
  }

  /**
   * To TOC.
   */
  toTOC(): void {
    this.termService.open();
  }

  /**
   * Will dismiss.
   */
  willDismiss(): void {
    const modal = this.modal();
    if (modal) modal.showBackdrop = false;
  }
}
